import { z } from 'zod';
import { ConversationIdSchema, ScheduledTaskIdSchema } from '../ids.ts';

export const TOOL_CREATE_SCHEDULED_TASK = 'create_scheduled_task';
export const TOOL_UPDATE_SCHEDULED_TASK = 'update_scheduled_task';
export const TOOL_LIST_SCHEDULED_TASKS = 'list_scheduled_tasks';
export const TOOL_DELETE_SCHEDULED_TASK = 'delete_scheduled_task';
export const TOOL_FINISH_TASK_RUN = 'finish_task_run';
export const SCHEDULED_TASK_TOOL_NAMES: readonly string[] = [
  TOOL_CREATE_SCHEDULED_TASK,
  TOOL_UPDATE_SCHEDULED_TASK,
  TOOL_LIST_SCHEDULED_TASKS,
  TOOL_DELETE_SCHEDULED_TASK,
  TOOL_FINISH_TASK_RUN,
];

export const SCHEDULED_TASK_TITLE_MAX_CHARS = 120;
export const SCHEDULED_TASK_PROMPT_MAX_CHARS = 8000;
export const SCHEDULED_TASKS_PER_USER_MAX = 20;
export const TASK_RUN_SUMMARY_MAX_CHARS = 2000;
export const TASK_RUN_PROBLEMS_MAX = 10;
export const TASK_RUN_PROBLEM_MAX_CHARS = 300;

export const SCHEDULED_TASK_STATUSES = ['proposed', 'active', 'paused', 'cancelled'] as const;
export const ScheduledTaskStatusSchema = z.enum(SCHEDULED_TASK_STATUSES);
export type ScheduledTaskStatus = z.infer<typeof ScheduledTaskStatusSchema>;

export const SCHEDULED_TASK_PAUSE_REASONS = ['user', 'failures'] as const;
export const ScheduledTaskPauseReasonSchema = z.enum(SCHEDULED_TASK_PAUSE_REASONS);
export type ScheduledTaskPauseReason = z.infer<typeof ScheduledTaskPauseReasonSchema>;

export const TASK_RUN_STATUSES = ['running', 'done', 'partial', 'failed', 'missed'] as const;
export const TaskRunStatusSchema = z.enum(TASK_RUN_STATUSES);
export type TaskRunStatus = z.infer<typeof TaskRunStatusSchema>;

export const TASK_RUN_REPORTED_STATUSES = ['done', 'partial', 'failed'] as const;
export type TaskRunReportedStatus = (typeof TASK_RUN_REPORTED_STATUSES)[number];

export const ScheduleDefinitionSchema = z.object({
  title: z.string().trim().min(1).max(SCHEDULED_TASK_TITLE_MAX_CHARS),
  prompt: z.string().trim().min(1).max(SCHEDULED_TASK_PROMPT_MAX_CHARS),
  schedule: z.string().trim().min(9).max(120),
  timezone: z.string().min(1).max(64),
});
export type ScheduleDefinition = z.infer<typeof ScheduleDefinitionSchema>;

export const PendingScheduleChangeSchema = ScheduleDefinitionSchema.partial();
export type PendingScheduleChange = z.infer<typeof PendingScheduleChangeSchema>;

export const TaskRunSummarySchema = z.object({
  status: TaskRunStatusSchema,
  occurrence_at: z.string(),
  conversation_id: ConversationIdSchema.nullable(),
  summary: z.string().nullable(),
});
export type TaskRunSummary = z.infer<typeof TaskRunSummarySchema>;

export const ScheduledTaskViewSchema = z.object({
  id: ScheduledTaskIdSchema,
  title: z.string(),
  prompt: z.string(),
  schedule: z.string(),
  timezone: z.string(),
  status: ScheduledTaskStatusSchema,
  pause_reason: ScheduledTaskPauseReasonSchema.nullable(),
  pending: PendingScheduleChangeSchema.nullable(),
  next_run_at: z.string().nullable(),
  last_run: TaskRunSummarySchema.nullable(),
  created_at: z.string(),
});
export type ScheduledTaskView = z.infer<typeof ScheduledTaskViewSchema>;

export const CreateScheduledTaskInputSchema = z.object({
  title: z.string(),
  prompt: z.string(),
  schedule: z.string(),
  timezone: z.string().optional(),
});
export type CreateScheduledTaskInput = z.infer<typeof CreateScheduledTaskInputSchema>;

export const UpdateScheduledTaskInputSchema = z.object({
  task_id: z.string(),
  title: z.string().optional(),
  prompt: z.string().optional(),
  schedule: z.string().optional(),
  timezone: z.string().optional(),
  paused: z.boolean().optional(),
});
export type UpdateScheduledTaskInput = z.infer<typeof UpdateScheduledTaskInputSchema>;

export const DeleteScheduledTaskInputSchema = z.object({ task_id: z.string() });
export type DeleteScheduledTaskInput = z.infer<typeof DeleteScheduledTaskInputSchema>;

export const FinishTaskRunInputSchema = z.object({
  status: z.enum(TASK_RUN_REPORTED_STATUSES),
  summary: z.string().trim().min(1).max(TASK_RUN_SUMMARY_MAX_CHARS),
  problems: z
    .array(z.string().trim().min(1).max(TASK_RUN_PROBLEM_MAX_CHARS))
    .max(TASK_RUN_PROBLEMS_MAX)
    .default([]),
});
export type FinishTaskRunInput = z.infer<typeof FinishTaskRunInputSchema>;

export const SCHEDULED_TASK_ERROR_CODES = [
  'invalid_tool_input',
  'invalid_schedule',
  'not_found',
  'limit_reached',
  'unavailable',
] as const;
export type ScheduledTaskErrorCode = (typeof SCHEDULED_TASK_ERROR_CODES)[number];

const ScheduledTaskToolErrorSchema = z.object({
  type: z.literal('scheduled_task_error'),
  error_code: z.enum(SCHEDULED_TASK_ERROR_CODES),
  message: z.string(),
});
export type ScheduledTaskToolError = z.infer<typeof ScheduledTaskToolErrorSchema>;

export const SCHEDULED_TASK_ACTIONS = [
  'proposed',
  'change_proposed',
  'updated',
  'deleted',
] as const;

export const ScheduledTaskOutputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('scheduled_task'),
    action: z.enum(SCHEDULED_TASK_ACTIONS),
    task: ScheduledTaskViewSchema,
    next_runs: z.array(z.string()),
  }),
  ScheduledTaskToolErrorSchema,
]);
export type ScheduledTaskOutput = z.infer<typeof ScheduledTaskOutputSchema>;

export const ListScheduledTasksOutputSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('scheduled_tasks'), tasks: z.array(ScheduledTaskViewSchema) }),
  ScheduledTaskToolErrorSchema,
]);
export type ListScheduledTasksOutput = z.infer<typeof ListScheduledTasksOutputSchema>;

export const FinishTaskRunOutputSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('task_run_reported'), status: z.enum(TASK_RUN_REPORTED_STATUSES) }),
  ScheduledTaskToolErrorSchema,
]);
export type FinishTaskRunOutput = z.infer<typeof FinishTaskRunOutputSchema>;
